import React, { useState } from "react";
import Button from "@surgia/ui/components/button";
import checkedIcon from "../../icons/checkbox/checked.svg";
import uncheckedIcon from "../../icons/checkbox/unchecked.svg";
import productIcon from "../../icons/catalog/product.svg";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { productQueries } from "../../../../api/src/queryStore/admin/product";
import { toast } from "sonner";

interface ProductVariant {
  id: string;
  name: string;
  isSelected: boolean;
}

interface ProductCardProps {
  product: {
    id: string;
    name: string;
    variantCount: number;
    image?: string;
    isSelected: boolean;
    variants: ProductVariant[];
    secondId?: string;
    parentProductCurrentlyAssigned: boolean;
  };
  tenantId?: string; // Add this prop
  // New prop for parent product assignment
  onAssignParentProduct?: (productId: string) => Promise<void>;
  isAssigningParentProduct?: boolean;
}

const ProductCard: React.FC<ProductCardProps> = ({
  product,
  tenantId, // Use the prop
  // onToggleSelection,
  // onAssignParentProduct,
  // isAssigningParentProduct = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const queryClient = useQueryClient();

  // Fetch variants for the expanded product
  const { data: productVariationsResponse, isLoading: isLoadingVariations } =
    useQuery({
      ...productQueries.queries.productVariations(
        product?.secondId || "",
        tenantId!
      ),
      enabled: !!product.secondId && product?.variantCount > 0,
    });
  // Mutation for assigning product variants
  const assignVariantsMutation = useMutation({
    ...productQueries.mutations.assignProductVariant(tenantId || ""),
    onSuccess: () => {
      toast.success("Changes saved successfully!");
      // Reset local state after successful save
      setToggledVariants(new Set());
      setIsOpen(false);
      queryClient.invalidateQueries({
        queryKey: productQueries.queries.productVariations(
          product?.secondId || "",
          tenantId || ""
        ).queryKey,
      });
    },
    onError: () => {
      toast.error("Failed to save changes. Please try again.");
    },
  });

  // Track which variants have been toggled from their initial state
  const [toggledVariants, setToggledVariants] = useState<Set<string>>(
    new Set()
  );

  // Helper function to determine if a variant should be visually selected
  const isVariantSelected = (productId: string, initialIsAssigned: boolean) => {
    const hasBeenToggled = toggledVariants.has(productId);
    return hasBeenToggled ? !initialIsAssigned : initialIsAssigned;
  };

  const handleVariantToggle = (productId: string) => {
    // Track that this variant has been toggled
    setToggledVariants((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(productId)) {
        newSet.delete(productId);
      } else {
        newSet.add(productId);
      }
      return newSet;
    });
  };

  const handleCancel = () => {
    // Reset selections and close the card
    setToggledVariants(new Set());
    setIsOpen(false);
  };

  const handleSave = () => {
    if (toggledVariants.size === 0) {
      toast.info("No changes to save.");
      return;
    }

    // Get the product IDs from the toggled variants
    const toggledProductIds = Array.from(toggledVariants);

    if (toggledProductIds.length === 0) {
      toast.error("No valid product IDs found for toggled variants.");
      return;
    }

    // Call the mutation with the payload - this will toggle assignment state
    assignVariantsMutation.mutate({
      productIds: toggledProductIds.map((id) => parseInt(id)),
      parentProductId: parseInt(product?.id),
    });
  };

  if (!isOpen) {
    return (
      <div
        className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 cursor-pointer hover:shadow-md transition-shadow"
        onClick={() => setIsOpen(true)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {/* <img
              src={
                product.parentProductCurrentlyAssigned
                  ? checkedIcon
                  : uncheckedIcon
              }
              alt={""}
              onClick={handleParentProductToggle}
              className={`cursor-pointer ${
                isAssigningParentProduct ? "opacity-50" : ""
              }`}
            /> */}
            <div className="w-8 h-8 rounded-full  flex items-center justify-center">
              <img
                src={product.image ?? productIcon}
                alt="surgiaProductImage"
                className="rounded-full w-8 h-8 shadow-[0_0_0_0.5px_rgba(0,0,0,0.1)]"
              />{" "}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-gray-900 font-medium">{product.name}</span>
            </div>
          </div>
          <svg
            className="w-4 h-4 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </div>
      </div>
    );
  }

  // Open State
  return (
    <div className="bg-gray-50 rounded-lg border border-gray-200 shadow-sm">
      {/* Header */}
      <div
        className=" bg-white px-4 py-3 rounded-t-lg flex items-center justify-between"
        onClick={() => setIsOpen(false)}
      >
        <div className="flex items-center gap-3">
          {/* <img
            src={
              product.parentProductCurrentlyAssigned
                ? checkedIcon
                : uncheckedIcon
            }
            alt={""}
            onClick={handleParentProductToggle}
            className={`cursor-pointer ${
              isAssigningParentProduct ? "opacity-50" : ""
            }`}
          /> */}
          <div className="w-8 h-8 rounded-full  flex items-center justify-center">
            <img
              src={product.image ?? productIcon}
              alt="surgiaProductImage"
              className="rounded-full w-8 h-8 shadow-[0_0_0_0.5px_rgba(0,0,0,0.1)]"
            />{" "}
          </div>
          <span className="text-gray-900 font-medium">{product.name}</span>
        </div>
        <svg
          className="w-4 h-4 text-gray-400 cursor-pointer"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M5 15l7-7 7 7"
          />
        </svg>
      </div>

      {/* Content */}
      <div className="flex flex-col items-start px-4 py-6 gap-5 w-full bg-[#FAFAFA]">
        {/* Variants List */}
        <div className="w-full">
          <div className="space-y-2">
            {isLoadingVariations ? (
              <div className="text-sm text-gray-500 italic">
                Loading variants...
              </div>
            ) : productVariationsResponse?.length > 0 ? (
              productVariationsResponse
                .filter(
                  (tenantProduct: any) =>
                    tenantProduct.catalog_info?.id.toString() !== product?.id
                )
                .map((tenantProduct: any) => {
                  // Format feature values for display - similar to other components
                  const featureDisplay =
                    tenantProduct?.catalog_info?.feature_values
                      ?.map(
                        (featureValue: any) =>
                          featureValue.featureVariant.variant_name
                      )
                      .join(", ") || "";

                  return (
                    <div
                      key={tenantProduct?.id}
                      className="flex items-center gap-3"
                    >
                      <img
                        src={
                          isVariantSelected(
                            tenantProduct.catalog_info?.id.toString(),
                            tenantProduct.currently_assigned
                          )
                            ? checkedIcon
                            : uncheckedIcon
                        }
                        alt="variant selection"
                        onClick={() =>
                          handleVariantToggle(
                            tenantProduct.catalog_info?.id.toString()
                          )
                        }
                        className="cursor-pointer"
                      />
                      <div className="flex-1">
                        <div className="text-sm text-gray-500 mt-1">
                          {featureDisplay}
                        </div>
                      </div>
                    </div>
                  );
                })
            ) : (
              <div className="text-sm text-gray-500 italic">
                No variants available
              </div>
            )}
          </div>
        </div>

        {/* Separator */}
        <div className="w-full border-t border-gray-200"></div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-2 w-full">
          <Button
            variant="outline"
            onClick={handleCancel}
            disabled={assignVariantsMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={
              assignVariantsMutation.isPending || toggledVariants.size === 0
            }
            className={
              toggledVariants.size === 0 ? "opacity-50 cursor-not-allowed" : ""
            }
          >
            {assignVariantsMutation.isPending ? "Saving..." : "Save"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ProductCard;
